import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Injector, runInInjectionContext, untracked, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EntityView, EntityViewCard, EntityViewDisplay, EntityViewSort, EntityViewSortDirection, MetadataEntityData } from '@jms/model';
import { KanbanComponent, KanbanDbClickParams, KanbanEditorType, KanbanItemTemplateDirective, KanbanSwimlane } from '@workbuddy/ui/v2/components/kanban';
import { EntityBoardContext } from '../../context';
import { EntityBoardStore } from '../../state';
import { resolveBoardRowId, resolveBoardRowScopePaths } from '../../reorder';
import { EntityCardComponent } from '../entity.card';
import { BoardEmptyComponent } from '../board.empty';
import { revealFocusedBoardCard } from '../board.focus/board.focus.scroll';
import { boardCardVisibleOrder } from './board.card.visible.order';

@Component({
  selector: 'board-card',
  templateUrl: './board.card.component.html',
  styleUrls: ['./board.card.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, KanbanComponent, KanbanItemTemplateDirective, EntityCardComponent, BoardEmptyComponent],
})
export class BoardCardComponent {
  protected readonly KanbanEditorType = KanbanEditorType;
  private readonly store = inject(EntityBoardStore);
  private readonly injector = inject(Injector);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly $context = computed(() => this.store.$context() as EntityBoardContext);
  protected readonly $view = computed(() => this.store.$effectiveView());
  protected readonly $metadata = computed(() => this.store.$metadata() as MetadataEntityData | null);
  protected readonly $items = computed(() => this.store.$items());
  protected readonly $display = computed(() => this.store.$effectiveDisplay());
  /**
   * With a panel, the highlighted card is the one the panel shows — a step moves focus without a tap,
   * and the kanban only learns of that through this input.
   */
  protected readonly $selectedItem = computed(() => {

    if (this.$context()?.itemInteraction?.selectable || !this.store.$panelKind()) return this.store.$selectedItem();
    const focusedId = this.store.$focusedItemId();
    return focusedId ? this.store.$itemsByKey().get(focusedId) ?? null : null;
  });
  protected readonly $selectedRowKeys = computed(() => this.store.$selectedRowKeys());
  protected readonly $cardConfig = computed(() => this.resolveCardConfig(this.$view(), this.$display()));
  protected readonly $swimlanes = computed<KanbanSwimlane[]>(() => this.buildSwimlanes(this.$items(), this.$view()));

  /** Row identity — a parent-shaped row's root `_id` is its PARENT's, shared by every sibling. */
  protected readonly $itemKey = computed(() => {

    const scopes = resolveBoardRowScopePaths(this.store.$contextRecordScopes(), this.$metadata());
    return (item: any) => resolveBoardRowId(item, scopes);
  });

  private readonly $visibleOrder = computed(() => boardCardVisibleOrder(this.$swimlanes(), this.$itemKey()));

  public constructor() {

    this.destroyRef.onDestroy(this.store.registerVisibleOrder(this.$visibleOrder));

    effect(() => {
      const stepped = this.store.$steppedItem();
      if (stepped) untracked(() => revealFocusedBoardCard(this.hostEl.nativeElement, stepped.id));
    });
  }

  protected onItemSelected(item: any): void {

    const context = this.$context();
    const interaction = context?.itemInteraction;

    // `w-kanban` reports a deselect as a null item — it owns the toggle. Passing that into
    // `toggleSelectedItem` would clear the whole selection instead of the one card.
    if (!item) {
      if (this.store.$panelKind()) this.store.whenPanelCanLeave(() => this.store.setFocusedItemId(null));
      return;
    }

    if (interaction?.selectable) {
      this.store.toggleSelectedItem(item);
      return;
    }

    // With a panel open the click drives the panel instead of navigating away — the record is
    // already being shown beside the collection, so leaving the board would undo that.
    if (item && this.store.$panelKind()) {
      const rowId = this.$itemKey()(item);
      // Clicking the focused item again deselects it, closing a selection-driven quickview.
      const next = rowId === this.store.$focusedItemId() ? null : rowId;
      this.store.whenPanelCanLeave(() => this.store.setFocusedItemId(next));
      return;
    }

    if (item && interaction?.navigate && !interaction?.selectable) {
      // Injection context so manifest navigate callbacks may `inject()` (e.g. EntityInspectors).
      runInInjectionContext(this.injector, () => interaction.navigate!(item));
    }
  }

  /**
   * Double tap / long press opens the whole record, matching the kanban and the table.
   *
   * A touch operator has no other way off a card board: a tap selects or drives the panel, and
   * neither leaves the board. The mount's own `navigate` wins, then its `onExpand` overlay.
   */
  protected onItemDblClicked(event: KanbanDbClickParams): void {

    const item = event?.item;
    if (!item) return;

    const interaction = this.$context()?.itemInteraction;

    if (interaction?.navigate) {
      // Injection context so manifest navigate callbacks may `inject()` (e.g. EntityInspectors).
      runInInjectionContext(this.injector, () => interaction.navigate!(item));
      return;
    }

    if (interaction?.onExpand) {
      void runInInjectionContext(this.injector, () => interaction.onExpand!(item));
    }
  }

  /** Per-card, per-CD-pass, so it stays a Set lookup — see `withSelectionState.$selectedRowKeys`. */
  protected isSelected(item: any): boolean {

    if (!item) return false;

    return this.$selectedRowKeys().has(this.$itemKey()(item));
  }

  private buildSwimlanes(items: any[], view: EntityView | null): KanbanSwimlane[] {

    const sortedItems = this.sortItems(items ?? [], this.resolveSort(view, this.$display()));

    return [{
      name: 'All',
      columns: [{
        key: 'all',
        name: 'All',
        items: sortedItems,
        hasItems: sortedItems.length > 0,
      }],
    }];
  }

  private resolveCardConfig(view: EntityView | null, display: EntityViewDisplay): Partial<EntityViewCard> | null {

    if (!view) return null;

    if (display === EntityViewDisplay.Card) {
      return view.card2?.card ?? {};
    }

    if (display === EntityViewDisplay.Board) {
      return view.board?.card ?? null;
    }

    return null;
  }

  private resolveSort(view: EntityView | null, display: EntityViewDisplay): EntityViewSort | null {

    if (!view) {
      return null;
    }

    if (display === EntityViewDisplay.Card) {
      const card2 = view.card2;
      return card2?.sortBy ? { _id: card2.sortBy, direction: card2.sortOrder ?? EntityViewSortDirection.Asc } : null;
    }

    const board = display === EntityViewDisplay.Board ? view.board : null;
    return board?.sortBy ? { _id: board.sortBy, direction: board.sortOrder ?? EntityViewSortDirection.Asc } : null;
  }

  private sortItems(items: any[], sort: EntityViewSort | null | undefined): any[] {

    const path = sort?._id;

    if (!path) {
      return items;
    }

    const direction = sort?.direction ?? EntityViewSortDirection.Asc;
    const multiplier = direction === EntityViewSortDirection.Desc ? -1 : 1;

    return [...items].sort((left, right) => {
      const a = this.toComparableValue(this.getValueFromPath(left, path));
      const b = this.toComparableValue(this.getValueFromPath(right, path));

      if (a == null && b == null) {
        return 0;
      }

      if (a == null) {
        return 1;
      }

      if (b == null) {
        return -1;
      }

      if (a < b) {
        return -1 * multiplier;
      }

      if (a > b) {
        return 1 * multiplier;
      }

      return 0;
    });
  }

  private getValueFromPath(item: any, path: string | null | undefined): any {

    if (!item || !path) {
      return null;
    }

    return path.split('.').reduce((acc, part) => acc?.[part], item);
  }

  private toComparableValue(value: any): string | number | null {

    if (value == null) {
      return null;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      return value.toLowerCase();
    }

    if (typeof value === 'boolean') {
      return value ? 1 : 0;
    }

    if (Array.isArray(value)) {
      return value.map(item => this.stringifyValue(item)).join(', ').toLowerCase();
    }

    if (typeof value === 'object') {
      return this.stringifyValue(value).toLowerCase();
    }

    return String(value).toLowerCase();
  }

  private stringifyValue(value: any): string {

    if (value == null) {
      return '';
    }

    if (Array.isArray(value)) {
      return value.map(item => this.stringifyValue(item)).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
      const label = value.name ?? value.label ?? value.displayName ?? value.systemName ?? value.systemStatus ?? value.value;

      if (label != null && label !== '') {
        return String(label);
      }
    }

    return String(value);
  }
}
